import { DateTime } from 'luxon';
import { observer } from 'mobx-react';
import * as React from 'react';
import {
  DateInput,
  FormCell,
  Grid,
  GridItem,
  IDateInputValue,
  ISelectOption,
  NumberInput,
  Select,
  TextInput,
} from 'ui-toolkit';
import { DeedDocumentType } from '../classes/DeedDocumentType';
import { EstateType } from '../classes/EstateType';
import { IRecordedDeedRequired, IRecordedDeed } from '../classes/RecordedDeed';
import getOptionFromSnakeCase from '../utils/getOptionFromSnakeCase';
import getOptionsListFromSnakeCase from '../utils/getOptionsListFromSnakeCase';
import { TimeInput } from './fields/TimeInput/TimeInput';

interface IRecordedDeedEditProps {
  data: IRecordedDeed;
  requiredFields: IRecordedDeedRequired;
  errorRecordedDeed: any;
  onRecordedDeedChange: (value: IRecordedDeed, propName: string, error?: string | null) => void;
}

const RecordedDeedEdit: React.FunctionComponent<IRecordedDeedEditProps> = observer((props) => {
  const onSelect = (propName: string, value: ISelectOption) => {
    onChange(propName, value.value, null);
  };

  const onChange = (propName: string, value: any, error?: string | null) => {
    const newValue = {
      ...props.data,
      [propName]: value === '' ? null : value,
    };
    props.onRecordedDeedChange(newValue, propName, error);
  };

  const onDateChange = (propName: string, value: IDateInputValue) => {
    const newValue = {
      ...props.data,
      [propName]: value.value,
    };
    props.onRecordedDeedChange(newValue, propName, value.error);
  };

  return (
    <>
      <Grid columnCount={2}>
        <TextInput
          label={'Grantee(s)'}
          errorText={props.errorRecordedDeed.grantee}
          placeholder={'Serena Williams'}
          value={props.data.grantee}
          isRequired={props.requiredFields.grantee}
          onChange={onChange.bind(props, 'grantee')}
        />
        <TextInput
          label={'Grantor(s)'}
          errorText={props.errorRecordedDeed.grantor}
          placeholder={'Venus Williams'}
          value={props.data.grantor}
          isRequired={props.requiredFields.grantor}
          onChange={onChange.bind(props, 'grantor')}
        />
      </Grid>
      <Grid columnCount={props.requiredFields.other_document_type ? 3 : 2}>
        <FormCell
          label={'Document type'}
          isRequired={props.requiredFields.document_type}
          errorText={props.errorRecordedDeed.document_type}
        >
          <Select
            onChange={onSelect.bind(props, 'document_type')}
            value={getOptionFromSnakeCase(props.data.document_type)}
            options={getOptionsListFromSnakeCase(Object.keys(DeedDocumentType))}
          />
        </FormCell>
        {props.requiredFields.other_document_type ? (
          <TextInput
            label={'Other Document type'}
            errorText={props.errorRecordedDeed.other_document_type}
            placeholder={'Other Document type'}
            value={props.data.other_document_type}
            isRequired={props.requiredFields.other_document_type}
            onChange={onChange.bind(props, 'other_document_type')}
          />
        ) : null}
        <FormCell
          label={'Estate type'}
          isRequired={props.requiredFields.estate_type}
          errorText={props.errorRecordedDeed.estate_type}
        >
          <Select
            onChange={onSelect.bind(props, 'estate_type')}
            value={getOptionFromSnakeCase(props.data.estate_type)}
            options={getOptionsListFromSnakeCase(Object.keys(EstateType))}
          />
        </FormCell>
      </Grid>
      <Grid columnCount={2}>
        <NumberInput
          label={'Consideration'}
          errorText={props.errorRecordedDeed.consideration}
          isRequired={props.requiredFields.consideration}
          value={props.data.consideration}
          mode={'currency'}
          prefix={'$'}
          placeholder={'128,000.00'}
          onChange={onChange.bind(props, 'consideration')}
        />
        <DateInput
          label={'Execution date'}
          errorText={props.errorRecordedDeed.execution_date}
          maxDate={DateTime.local().toISODate()}
          value={props.data.execution_date}
          isRequired={props.requiredFields.execution_date}
          onChange={onDateChange.bind(props, 'execution_date')}
        />
      </Grid>
      <Grid columnCount={4}>
        <DateInput
          label={'Recorded date'}
          isRequired={props.requiredFields.recorded_date}
          errorText={props.errorRecordedDeed.recorded_date}
          minDate={props.data.execution_date ? props.data.execution_date : undefined}
          maxDate={DateTime.local().toISODate()}
          value={props.data.recorded_date}
          onChange={onDateChange.bind(props, 'recorded_date')}
        />
        <TimeInput
          label={'Recorded time'}
          isRequired={props.requiredFields.recorded_at}
          errorText={props.errorRecordedDeed.recorded_at}
          inputValue={props.data.recorded_at}
          onChange={onChange.bind(props, 'recorded_at')}
        />
        <GridItem columnStart={3} columnEnd={5}>
          <FormCell hintText={'** Instrument No. or Book/Page is required'}>
            <Grid columnCount={2}>
              <TextInput
                label={'Instrument No. **'}
                errorText={props.errorRecordedDeed.instrument_number}
                placeholder={'012345'}
                isRequired={props.requiredFields.instrument_number}
                value={props.data.instrument_number}
                onChange={onChange.bind(props, 'instrument_number')}
              />

              <TextInput
                label={'Book/Page **'}
                placeholder={'012345'}
                isRequired={props.requiredFields.book}
                errorText={props.errorRecordedDeed.book}
                value={props.data.book}
                onChange={onChange.bind(props, 'book')}
              />
            </Grid>
          </FormCell>
        </GridItem>
      </Grid>
    </>
  );
});

export default RecordedDeedEdit;
